import { Button } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { MaterialTableComponent } from '../../../Components/shared/ReusableSmallComponents';
import { selectLang } from '../../../Contexts/lang/langueSlice';
import {getStatusCraLabel } from '../../../data/Interfaces';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useIsExpertEsn from '../../../hooks/useIsExpertEsn';
import { useNumberFormatting } from '../../../hooks/useNumberFormatting';
import useRole from '../../../hooks/useRole';
import useUtilities from '../../../hooks/useUtilities';
import { formatDateJJMMYYYY } from '../../../Utils/dateFormatter';
import { getNomPrenomClearFromPersoData, sortDate } from '../../../Utils/utils';
import { navigate2CraCraX } from '../../Admin/Mission/AllCraAdmin';

const AllCraExpert = () => {
    const { addEuro4Decimal } = useNumberFormatting();
    const { i18n } = useTranslation();
    const lang = useSelector(selectLang);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const isEsnExpert=useIsExpertEsn();
    const role = useRole();
    const { setBackdropOpen} = useUtilities();
    const craColumnsExpert = [
        {
            field: 'noCra',
            title: i18n.t('Réf.CRA'),
            render: (rowData) => (
                <Button
                    variant="text"
                    onClick={() => navigate2CraCraX({rowData, role,lang,isEsn:(role=='esn' ? true:false),isAchat:(role=='esn' ? false:true), navigate})}
                >
                    {rowData?.noCra}
                </Button>
            ),
        },
        {
            field: 'intituleMission',
            title: i18n.t('Intitulé de la mission'),
        },
        {
            field: 'statutCraFinal',
            title: i18n.t('Statut du CRA'),
            render:  rowData =>
                i18n.t(rowData?.statutCraFinal)
        },
        {
            field: 'refClient',
            title: i18n.t('Réf.Client'),
        },
        {
            field: 'nomClient',
            title: i18n.t('Nom du Client'),
        },
        ...(role=='esn' ?
            [{
                field: 'nomExpert',
                title: i18n.t('Nom Expert'),
            }]:[]),
        {
            field: 'dateValidationExpert',
            title: i18n.t('Date de validation/Expert'),

        },
        {
            field: 'dateValidationClient',
            title: i18n.t('Date de validation/Client'),

        },
        {
            field: 'date',
            title: i18n.t('Période'),
        },
        {
            field: 'nbJour',
            title: i18n.t('Nb jours validés'),
            render: (rowData) => {
                return addEuro4Decimal(rowData?.nbJour,'j');
            }

        },
        {
            field: 'nbrJrsRestants',
            title: i18n.t('Nb.j. restants'),
            render: (rowData) => {
                return addEuro4Decimal(rowData?.nbrJrsRestants,'j');
            }
        },
        ...(!isEsnExpert
            ? [{

                field: 'tjExpert',
                title: i18n.t('Taux journalier HT'),
                render:  rowData =>
                    addEuro4Decimal(rowData?.tjExpert)
            },
            {
                field: 'totalAstreintes',
                title: i18n.t('Total Astreintes'),
                render:  rowData =>
                    addEuro4Decimal(rowData?.totalAstreintes)
            },
            {
                field: 'totalHno',
                title: i18n.t('Total HNO'),
                render:  rowData =>
                    addEuro4Decimal(rowData?.totalHno)
            },
            {
                field: 'totalNotesFrais',
                title: i18n.t('Total notes de frais'),
                render:  rowData =>
                    addEuro4Decimal(rowData?.totalNotesFrais)
            },
            {
                field: 'totalHtExpert',
                title: i18n.t('Total HT'),
                render:  rowData =>
                    addEuro4Decimal(rowData?.totalHtExpert)
            },]
            :[]),

    ];

    //=============================================================================
    let navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();

    const [craRows, setCraRows] = React.useState();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [url, setUrl] = useState('');
    const [filtreCraAvalider, setFiltreCraAvalider] = useState(searchParams.get('filtreCraAvalider') === 'true');

    useEffect(() => {
        const currentUrl = window.location.href;
        setUrl(currentUrl);
    }, [location]);

    useEffect(() => {
        if(url && !url.toLowerCase().includes('filtrecraavalider') ){
            setFiltreCraAvalider(false);
        }
    }, [url]);

    useEffect(() => {
        if(role)
            getMyCras();
    }, [filtreCraAvalider,role]);

    useEffect(() => {
        if(role)
            getMyCras();
    }, [role]);

    //=================================================================

    const isValidCra = cra => cra?.statutCra === 'pending' || cra?.statutCraX === 'pending';

    const createCra2Push = (cra, nbrJrsRestants) => {
        let craType = cra?.statutCraX ? 'CRAX' : 'CRA';
        return {
            craType,
            noCra: craType === 'CRAX' ? cra?.craXId : cra?.craId,
            intituleMission: cra?.missionId?.titre,
            statutCraFinal: getStatusCraLabel(cra?.statutCra || cra?.statutCraX),
            dateValidationExpert: formatDateJJMMYYYY(cra?.DateVE),
            dateValidationClient: formatDateJJMMYYYY(cra?.DateVC),
            Date: new Date(cra?.year, cra?.month - 1),
            nbrJrsRestants,
            date: dayjs(new Date(cra?.year, cra?.month - 1)).locale('fr').format('MMMM YYYY'),
            nbJour: cra?.nbrDays,
            tjExpert: cra?.tj,
            totalHtExpert: cra?.nbrDays ? (cra?.nbrDays * cra?.tj) : cra?.montantTotal,
            totalAstreintes: cra?.totalAstreintes,
            bdcId: cra?.bdcId?.bdcId,
            totalHno: cra?.totalHnos,
            totalNotesFrais: cra?.totalNotesFrais,
            refClient: cra?.missionId?.companyDataId?.clientId ?? cra?.missionId?.esnId?.esnId,
            nomClient: cra?.missionId?.companyDataId?.profileEntreprise?.nom ?? cra?.missionId?.esnId?.profileEntreprise?.nom,
            refExpert: cra?.missionId?.expertId?.expertId,
            nomExpert: getNomPrenomClearFromPersoData(cra?.missionId?.expertId?.persoData),
            ...cra,
        };
    };
    const getMyCras = async () => {
        setBackdropOpen(true);
        try {
            const response = await axiosPrivate.get(`/cra/getMyCras/${(role === 'esn' && location.pathname.includes('AllCrasVente')) ? '?isAchat=false' : ''}`, {
            // signal: controller.signal
            });

            let listCra = await response.data?.cras;
            let processedBdcIds = [];
            let tempArray = [];
            let nbrJrsRestants=0;
            if(filtreCraAvalider==true){
                listCra?.forEach(cra => {
                    if (isValidCra(cra)) {
                        nbrJrsRestants = cra?.bdcId?.nbJoursBDC;
                        // Process only if the bdcId has not been processed yet
                        if (!processedBdcIds.includes(cra?.bdcId?.bdcId)) {
                            // Mark the bdcId as processed
                            processedBdcIds.push(cra?.bdcId?.bdcId);
                            // Iterate through listCra to adjust nbrJrsRestants for matching bdcId
                            listCra?.forEach(prevCra => {
                                if (prevCra.bdcId?.bdcId === cra.bdcId?.bdcId && isValidCra(prevCra)) {
                                    nbrJrsRestants =  nbrJrsRestants - (prevCra.nbrDays ?? 0);
                                    // Add other Infos
                                    let cra2Push = { ...createCra2Push(prevCra,nbrJrsRestants), ...prevCra };
                                    // Push the cra2Push object to tempArray
                                    tempArray.push(cra2Push);
                                }
                            });
                        }
                    }
                });
            }
            else {
                listCra?.forEach(cra => {
                    nbrJrsRestants = cra?.bdcId?.nbJoursBDC;
                    // Process only if the bdcId has not been processed yet
                    if (!processedBdcIds.includes(cra?.bdcId?.bdcId)) {
                        // Mark the bdcId as processed
                        processedBdcIds.push(cra?.bdcId?.bdcId);
                        // Iterate through listCra to adjust nbrJrsRestants for matching bdcId
                        listCra?.forEach(prevCra => {
                            if (prevCra.bdcId?.bdcId === cra.bdcId?.bdcId){
                                nbrJrsRestants = nbrJrsRestants - (prevCra.nbrDays ?? 0);
                                let cra2Push = {...createCra2Push(prevCra,nbrJrsRestants), ...prevCra};
                                tempArray.push(cra2Push);
                            }
                        });
                    }
                });
            }
            tempArray=sortDate(tempArray);
            setCraRows(tempArray);
        } catch (error) {
            console.log(error);
        }
        setBackdropOpen(false);
    };

    return (
        <>

            <div className='frame5Profile'>
                {i18n.t('CRA & Factures')}
                {role === 'esn' && (
                    <>
                        &gt;
                        <span style={{ color: '#B0B0B0' }}>{i18n.t('Vente')}</span>
                    </>
                )}
                &gt;
                <span style={{ color: 'rgba(39, 45, 60, 1)' }}>{i18n.t('Liste des CRA')}</span>
            </div>
            <div className='HomeFrame'>
                <h4 className='titlesociete'>
                    {i18n.t('Liste des CRA')}
                </h4>

                <MaterialTableComponent
                    data={craRows}
                    columns={craColumnsExpert}
                    title="CRA"
                    //options={{filtering: true}}
                    onRowClick={(event, rowData) => {
                        setSelectedRow(rowData?.tableData.id);
                        // setRowSelected(rowData);
                        //console.log(rowData);
                        //if(rowData?.craType == 'CRAX'){
                        //    console.log({rowData});
                        //    navigate(`/${role}/CraX/${rowData?._id}`,
                        //        //* some infos for the expert update
                        //        {state:  rowData}
                        //    );
                        //}
                        //else if(rowData?.craType == 'CRA'){
                        //    navigate(`/${role}/Cra/${rowData?._id}`,);}
                    }}
                    selectedRow={selectedRow}
                />

                {/*<div className='Frame4AllAOs'>
                    <ThemeProvider theme={defaultMaterialTheme}>
                        <MaterialTable
                            icons={tableIcons}
                            data={craRows}
                            columns={craColumns}
                            title="CRA"
                            options={materialTableOptions}
                            onRowClick={(event, rowData) => {
                                // setRowSelected(rowData);
                                console.log(rowData);
                                if(rowData?.craType == 'CRAX'){
                                    console.log({rowData});
                                    navigate(`/${role}/CraX/${rowData?._id}`,
                                        //* some infos for the expert update
                                        {state:  rowData}
                                    );
                                }
                                else if(rowData?.craType == 'CRA'){
                                    navigate(`/${role}/Cra/${rowData?._id}`,);}

                            }}
                        />
                    </ThemeProvider>
                </div>*/}
            </div>
        </>
    );
};

export default AllCraExpert;